import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';

import '../../Styles/index.css';
import '../../Styles/Admin.css';
import izenuAPI from '../../services/izenuAPI';
import { titleWarning } from '../../utils/titleWarning';

const STATE_FILTERS = [
    { label: 'Todas', value: '' },
    { label: 'Pendentes', value: 'unreviewed' },
    { label: 'Aprovadas', value: 'approved' },
    { label: 'Rejeitadas', value: 'rejected' },
];

// Tracks marked as fully AI-generated earn no streaming royalties and stay out
// of the home shelves (see AiTrack.php). ?ai=appeal is the link in the
// appeal e-mail.
const AI_FILTERS = [
    { label: 'Feitas com IA', value: 'yes' },
    { label: 'IA contestadas', value: 'appeal' },
];

const formatDuration = (ms) => {
    if (!ms) return '--:--';
    const min = Math.floor(ms / 60000);
    const sec = Math.floor((ms % 60000) / 1000);
    return `${min}:${sec.toString().padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const StateBadge = ({ state }) => {
    const styles = {
        approved: 'text-blue-600 bg-blue-400/20 border-blue-500',
        rejected: 'text-rose-600 bg-rose-400/20 border-rose-500',
        unreviewed: 'text-amber-600 bg-amber-400/20 border-amber-500',
    };
    const labels = { approved: 'Aprovada', rejected: 'Rejeitada', unreviewed: 'Pendente' };
    const cls = styles[state] || styles.unreviewed;
    return (
        <span className={`text-xs font-semibold border rounded px-2 py-0.5 ${cls}`}>
            {labels[state] || state}
        </span>
    );
};

const ActiveBadge = ({ active }) => {
    if (active) {
        return <span className="text-xs font-semibold text-green-600 bg-green-400/20 border border-green-500 rounded px-2 py-0.5">Público</span>;
    }
    return <span className="text-xs font-semibold text-gray-500 bg-gray-400/20 border border-gray-400 rounded px-2 py-0.5">Oculto</span>;
};

const Player = ({ musicPlayer, playing, setPlaying }) => {
    if (!musicPlayer) return null;

    const cover = musicPlayer.cover_url?.original;

    return (
        <div className="border rounded-lg mb-4 bg-gray-900 p-3">
            <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                    {cover ? (
                        <img className="w-10 h-10 rounded border" src={cover} alt="" />
                    ) : (
                        <div className="w-10 h-10 rounded bg-gray-700" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-100 truncate">{musicPlayer.title}</p>
                    <p className="text-sm text-gray-400 truncate">{musicPlayer.singer || musicPlayer.name}</p>
                </div>
                <button
                    onClick={() => setPlaying(!playing)}
                    className="rounded-full flex items-center justify-center w-8 h-8 bg-amber-500 hover:bg-amber-400 transition"
                >
                    {playing ? (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path fillRule="evenodd" d="M8 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H8Zm7 0a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1Z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path fillRule="evenodd" d="M8.6 5.2A1 1 0 0 0 7 6v12a1 1 0 0 0 1.6.8l8-6a1 1 0 0 0 0-1.6l-8-6Z" clipRule="evenodd" />
                        </svg>
                    )}
                </button>
            </div>
            <ReactPlayer
                className="react-player"
                url={musicPlayer.file}
                width="100%"
                height="0"
                playing={playing}
            />
        </div>
    );
};

const EMPTY_FORM = { title: '', singer: '', genre: '', country: '', isrc: '', version: '', explicit: 0, track_number: '' };

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

const EditMusicModal = ({ music, onClose, onSaved }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [original, setOriginal] = useState(null);
    const [genres, setGenres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            izenuAPI.get('/admin/music/details.php', { params: { music_id: music.music_id } }),
            izenuAPI.get('/admin/genre/fetchAll.php').catch(() => ({ data: {} })),
        ]).then(([{ data: d }, { data: g }]) => {
            if (cancelled) return;
            const values = {
                ...EMPTY_FORM,
                ...Object.fromEntries(Object.keys(EMPTY_FORM).map(k => [k, d.data[k] ?? EMPTY_FORM[k]])),
            };
            setForm(values);
            setOriginal(values);
            setGenres((g.data || []).map(x => x.name).filter(Boolean));
        }).catch(err => {
            if (!cancelled) { setLoadFailed(true); setError(err?.response?.data?.message || 'Não foi possível carregar a música.'); }
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [music.music_id]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, saving]);

    const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        // Only what changed goes to the server, so an untouched empty ISRC or
        // track number is never re-validated.
        const changes = Object.fromEntries(
            Object.keys(form).filter(k => String(form[k] ?? '') !== String(original[k] ?? '')).map(k => [k, form[k]])
        );
        if (Object.keys(changes).length === 0) { onClose(); return; }
        setSaving(true);
        setError('');
        try {
            const { data: r } = await izenuAPI.post('/admin/music/update.php', { music_id: music.music_id, ...changes });
            onSaved({ ...music, ...r.updated });
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível guardar.');
            setSaving(false);
        }
    };

    // Keep the song's current genre selectable even if it is not in the table.
    const genreOptions = form.genre && !genres.includes(form.genre) ? [form.genre, ...genres] : genres;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => !saving && onClose()}>
            <form onSubmit={save} onClick={(e) => e.stopPropagation()}
                className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-4 sm:p-6 text-left shadow-xl">
                <div className="flex items-start gap-3 mb-4">
                    {music.cover_url?.original
                        ? <img src={music.cover_url.original} alt="" className="w-12 h-12 rounded object-cover border" />
                        : <div className="w-12 h-12 rounded bg-gray-200" />}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900">Editar música</h3>
                        <p className="text-sm text-gray-500 truncate">#{music.music_id} · {music.title}</p>
                    </div>
                    <button type="button" onClick={onClose} disabled={saving} aria-label="Fechar" className="p-1 text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
                </div>

                {loading ? (
                    <p className="py-8 text-center text-sm text-gray-500">A carregar…</p>
                ) : loadFailed ? null : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label className="sm:col-span-2 block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">Título</span>
                                <input className={inputCls} value={form.title} onChange={set('title')} maxLength={200} required />
                                {titleWarning(form.title) && (
                                    <span className="block mt-1 text-xs text-amber-700">{titleWarning(form.title)}</span>
                                )}
                            </label>
                            <label className="sm:col-span-2 block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">Artista (como aparece na faixa)</span>
                                <input className={inputCls} value={form.singer} onChange={set('singer')} maxLength={200} required />
                            </label>
                            <label className="block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">Género</span>
                                <select className={inputCls} value={form.genre} onChange={set('genre')}>
                                    <option value="">— sem género —</option>
                                    {genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">País</span>
                                <input className={inputCls} value={form.country} onChange={set('country')} maxLength={60} placeholder="Angola" />
                            </label>
                            <label className="block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">ISRC</span>
                                <input className={`${inputCls} uppercase`} value={form.isrc} onChange={set('isrc')} placeholder="AO-XXX-26-00001" />
                            </label>
                            <label className="block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">Versão</span>
                                <input className={inputCls} value={form.version} onChange={set('version')} maxLength={50} placeholder="original, remix, ao vivo…" />
                            </label>
                            <label className="block">
                                <span className="block mb-1 text-sm font-medium text-gray-700">Nº da faixa</span>
                                <input className={inputCls} type="number" min={1} max={999} value={form.track_number ?? ''} onChange={set('track_number')} />
                            </label>
                            <label className="flex items-center gap-2 sm:mt-6">
                                <input type="checkbox" checked={Number(form.explicit) === 1} onChange={set('explicit')} className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
                                <span className="text-sm text-gray-700">Conteúdo explícito</span>
                            </label>
                        </div>
                        <p className="mt-3 text-xs text-gray-500">
                            A capa, o ficheiro de áudio, o estado e a visibilidade não se mudam aqui.
                        </p>
                    </>
                )}

                {error && <p className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">{error}</p>}

                <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
                    <button type="submit" disabled={loading || saving || loadFailed} className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                        {saving ? 'A guardar…' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// What each row action does — shown above the table and as each button's tooltip.
const ACTION_HELP = {
    edit: 'Corrige título, género, país, versão e conteúdo explícito. Não muda o estado nem os ficheiros.',
    approve: 'A faixa passa a pública (se o lançamento estiver publicado) e o artista recebe um email.',
    reject: 'A faixa fica fora da plataforma. O artista vê "Recusada" no Estúdio.',
    ai: 'Totalmente gerada por IA: continua a tocar com a etiqueta "IA", sai das secções em destaque e deixa de receber royalties de streaming. Pede o motivo, que vai por email ao artista.',
    release: 'Abre o álbum/EP/single a que a faixa pertence.',
    showcase: 'Montra = home, playlists da semana, pushes e continuação da fila. Fica de fora automaticamente quem tem capa abaixo de 640 px, menos de 1 minuto ou título de ficheiro; aqui forças a entrada ou a saída. Fora da montra a faixa continua pública.',
};

const ACTION_LEGEND = [
    { label: 'Editar', cls: 'text-gray-700 bg-gray-100 border-gray-300', text: ACTION_HELP.edit },
    { label: 'Aprovar', cls: 'text-blue-600 bg-blue-400/20 border-blue-500', text: ACTION_HELP.approve },
    { label: 'Reprovar', cls: 'text-rose-600 bg-rose-400/20 border-rose-500', text: ACTION_HELP.reject },
    { label: 'Marcar IA', cls: 'text-violet-700 bg-violet-400/10 border-violet-400', text: ACTION_HELP.ai },
    { label: 'Montra', cls: 'text-teal-700 bg-teal-400/10 border-teal-500', text: ACTION_HELP.showcase },
    { label: 'Release', cls: 'text-purple-600 bg-purple-400/20 border-purple-500', text: ACTION_HELP.release },
];

export default function ListMusics() {
    const navigate = useNavigate();
    const [musics, setMusics] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // ?state=unreviewed opens straight on the queue (the link in the moderation e-mail).
    const [stateFilter, setStateFilter] = useState(() => {
        const q = new URLSearchParams(window.location.search).get('state');
        return STATE_FILTERS.some(f => f.value === q) ? q : '';
    });
    const [aiFilter, setAiFilter] = useState(() => {
        const q = new URLSearchParams(window.location.search).get('ai');
        return AI_FILTERS.some(f => f.value === q) ? q : '';
    });
    // ?showcase=out — approved tracks the montra leaves out (classes/Showcase.php).
    const [showcaseOut, setShowcaseOut] = useState(() => new URLSearchParams(window.location.search).get('showcase') === 'out');
    // ?user_id=X&artist=Nome — one artist's tracks (the "Ver músicas" button in Pré-inscrições).
    const [artistFilter, setArtistFilter] = useState(() => {
        const q = new URLSearchParams(window.location.search);
        const id = parseInt(q.get('user_id'), 10);
        return id > 0 ? { id, name: q.get('artist') || `#${id}` } : null;
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const [playing, setPlaying] = useState(false);
    const [musicPlayer, setMusicPlayer] = useState(null);

    const [modal, setModal] = useState(false);
    const [modalAction, setModalAction] = useState(null); // 'approved' | 'rejected'
    const [modalData, setModalData] = useState(null);

    const searchRef = useRef(null);
    const [editing, setEditing] = useState(null);

    const fetchMusics = useCallback(async (page, state, search, ai, artistId, outOfShowcase) => {
        setLoading(true);
        try {
            let url = `/admin/music/fetchAllMusics.php?page=${page}&limit=50`;
            if (state) url += `&state=${state}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (ai) url += `&ai=${ai}`;
            if (artistId) url += `&user_id=${artistId}`;
            if (outOfShowcase) url += '&showcase=out';

            const { data: resp } = await izenuAPI.get(url);
            if (resp.response) {
                setMusics(resp.musics);
                setTotal(resp.total);
                setTotalPages(resp.pages);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    }, []);

    // Debounced fetch on search/filter/page change
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchMusics(currentPage, stateFilter, searchTerm, aiFilter, artistFilter?.id, showcaseOut);
        }, searchTerm ? 400 : 0);
        return () => clearTimeout(timer);
    }, [currentPage, stateFilter, searchTerm, aiFilter, artistFilter, showcaseOut, fetchMusics]);

    const clearArtistFilter = () => {
        setArtistFilter(null);
        setCurrentPage(1);
        const q = new URLSearchParams(window.location.search);
        q.delete('user_id');
        q.delete('artist');
        const rest = q.toString();
        window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    };

    const toggleAiFilter = (value) => {
        setAiFilter(prev => (prev === value ? '' : value));
        setCurrentPage(1);
    };

    const setAiGenerated = async (music, flag) => {
        let reason = null;
        if (flag) {
            reason = window.prompt(
                `Marcar "${music.title}" como totalmente gerada por IA?\n\n` +
                'Deixa de receber royalties de streaming e sai das secções em destaque. ' +
                'O artista recebe um email com o motivo e pode contestar.\n\nMotivo:'
            );
            if (reason === null) return;
            if (!reason.trim()) { alert('Indica o motivo.'); return; }
        } else if (!window.confirm(`Retirar a marca de IA de "${music.title}"?`)) {
            return;
        }
        try {
            const { data } = await izenuAPI.post('/admin/music/setAiGenerated.php', {
                music_id: music.music_id, ai_generated: flag, reason,
            });
            if (!data.response) { alert(data.message || 'Erro'); return; }
            setMusics(prev => prev.map(m => m.music_id === music.music_id
                ? { ...m, ai_generated: flag ? 1 : 0, ai_flag_reason: reason, ai_appeal_at: null, ai_appeal_note: null }
                : m));
        } catch (error) {
            alert(error?.response?.data?.message || 'Erro ao gravar');
        }
    };

    // auto → follows the rules; in / out → the team decides.
    const setShowcase = async (music, mode) => {
        try {
            const { data } = await izenuAPI.post('/admin/music/setShowcase.php', { music_id: music.music_id, mode });
            if (!data.response) { alert(data.message || 'Erro'); return; }
            setMusics(prev => prev.map(m => m.music_id === music.music_id
                ? { ...m, showcase: data.showcase, showcase_reason: data.showcase_reason, showcase_override: data.showcase_override, showcase_label: data.showcase_label }
                : m));
        } catch (error) {
            alert(error?.response?.data?.message || 'Erro ao gravar');
        }
    };

    const handleFilterChange = (value) => {
        setStateFilter(value);
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const openModal = (music, action) => {
        setModalData(music);
        setModalAction(action);
        setModal(true);
    };

    const confirmAction = useCallback(async () => {
        if (!modalData || !modalAction) return;

        try {
            const { data: resp } = await izenuAPI.post('/music/changeState.php', {
                music_id: modalData.music_id,
                state: modalAction,
            });

            if (resp.response !== true) {
                alert('Erro ao atualizar estado. Tente mais tarde.');
                return;
            }

            // The pending counter in the menu.
            window.dispatchEvent(new Event('moderationchange'));

            // Update inline
            setMusics(prev =>
                prev.map(m =>
                    m.music_id === modalData.music_id ? { ...m, music_state: modalAction } : m
                )
            );

            // Send approval email if approved
            if (modalAction === 'approved' && modalData.email) {
                try {
                    await izenuAPI.post(`${process.env.REACT_APP_API_URL}/mail/sendApprovedEmail.php`, {
                        music_title: modalData.title,
                        user_email: modalData.email,
                    });
                } catch (emailErr) {
                    console.log('Email error:', emailErr);
                }
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao processar ação.');
        }

        setModal(false);
        setModalData(null);
        setModalAction(null);
    }, [modalData, modalAction]);

    const playMusic = (music) => {
        setMusicPlayer(music);
        setPlaying(true);
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#333', marginBottom: 8 }}>
                    Músicas
                </h2>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 12 }}>
                    Gestão de todas as músicas da plataforma. Pesquise, filtre por estado e aprove ou rejeite faixas.
                </p>
                <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
                    {total.toLocaleString('pt-PT')} {total === 1 ? 'música' : 'músicas'} {artistFilter ? 'deste artista' : 'na plataforma'}
                </span>
                {artistFilter && (
                    <div style={{ marginTop: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 6px 4px 12px', borderRadius: 16, background: '#EEF0FF', color: '#4338ca', fontSize: 13, fontWeight: 600 }}>
                            Artista: {artistFilter.name}
                            <button
                                onClick={clearArtistFilter}
                                title="Ver todas as músicas"
                                aria-label="Remover filtro de artista"
                                style={{ border: 'none', background: '#c7d2fe', color: '#4338ca', borderRadius: 10, width: 20, height: 20, lineHeight: '18px', cursor: 'pointer', fontSize: 14 }}
                            >
                                ×
                            </button>
                        </span>
                    </div>
                )}
            </div>

            {/* Player */}
            <Player musicPlayer={musicPlayer} playing={playing} setPlaying={setPlaying} />

            {/* Search + Filters */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                <div className="search-distributors-input" style={{ flex: 1, maxWidth: 400 }}>
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Pesquisar por título, artista..."
                        value={searchTerm}
                        onChange={handleSearch}
                        style={{ padding: '6px 0', fontSize: 14 }}
                    />
                </div>
                <div className="flex gap-2 flex-wrap">
                    {STATE_FILTERS.map(f => (
                        <button
                            key={f.value}
                            onClick={() => handleFilterChange(f.value)}
                            className={`text-sm font-semibold border rounded px-3 py-1.5 transition ${
                                stateFilter === f.value
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    {AI_FILTERS.map(f => (
                        <button
                            key={f.value}
                            onClick={() => toggleAiFilter(f.value)}
                            className={`text-sm font-semibold border rounded px-3 py-1.5 transition ${
                                aiFilter === f.value
                                    ? 'bg-violet-600 text-white border-violet-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-violet-400'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <button
                        onClick={() => { setShowcaseOut(v => !v); setCurrentPage(1); }}
                        title={ACTION_HELP.showcase}
                        className={`text-sm font-semibold border rounded px-3 py-1.5 transition ${
                            showcaseOut
                                ? 'bg-teal-600 text-white border-teal-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'
                        }`}
                    >
                        Fora da montra
                    </button>
                </div>
            </div>

            {/* What the action buttons do */}
            <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3" style={{ fontSize: 13, color: '#555' }}>
                <div style={{ fontWeight: 600, color: '#333', marginBottom: 6 }}>O que faz cada acção</div>
                <ul className="grid gap-x-6 gap-y-1.5 md:grid-cols-2">
                    {ACTION_LEGEND.map(a => (
                        <li key={a.label} className="flex items-start gap-2">
                            <span className={`text-xs border py-0.5 px-2 rounded whitespace-nowrap ${a.cls}`}>{a.label}</span>
                            <span style={{ lineHeight: 1.5 }}>{a.text}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin fill-amber-400" viewBox="0 0 100 101" fill="none">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <p className="text-amber-500 font-light text-lg mt-3">A carregar...</p>
                </div>
            ) : musics.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <p className="text-gray-500 text-lg">Nenhuma música encontrada</p>
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                                <tr>
                                    <th className="px-3 py-3">#</th>
                                    <th className="px-3 py-3">Capa</th>
                                    <th className="px-3 py-3">Título / Artista</th>
                                    <th className="px-3 py-3 hidden lg:table-cell">Género</th>
                                    <th className="px-3 py-3 hidden md:table-cell">Duração</th>
                                    <th className="px-3 py-3 hidden md:table-cell">Plays</th>
                                    <th className="px-3 py-3 hidden lg:table-cell">Upload</th>
                                    <th className="px-3 py-3">Estado</th>
                                    <th className="px-3 py-3 hidden lg:table-cell">Visibilidade</th>
                                    <th className="px-3 py-3">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {musics.map((m, i) => {
                                    const cover = m.cover_url?.original;
                                    const rowNum = (currentPage - 1) * 50 + i + 1;

                                    return (
                                        <tr key={m.music_id} className="hover:bg-gray-50 transition">
                                            <td className="px-3 py-2 text-gray-400 text-xs">{rowNum}</td>
                                            <td className="px-3 py-2">
                                                <div
                                                    onClick={() => playMusic(m)}
                                                    className="cursor-pointer"
                                                >
                                                    {cover ? (
                                                        <img className="w-10 h-10 rounded border object-cover" src={cover} alt="" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded bg-gray-200" />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div
                                                    onClick={() => playMusic(m)}
                                                    className="cursor-pointer"
                                                >
                                                    <p className="font-medium text-gray-900 truncate max-w-[200px]">
                                                        {m.ai_generated === 1 && (
                                                            <span className="mr-1 text-[10px] font-bold text-violet-700 bg-violet-100 border border-violet-300 rounded px-1 align-middle">IA</span>
                                                        )}
                                                        {m.title}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{m.singer || m.name}</p>
                                                </div>
                                                {m.music_state === 'approved' && m.active === 1 && m.showcase === 0 && (
                                                    <p className="mt-0.5 text-xs text-teal-700 max-w-[260px]" title={ACTION_HELP.showcase}>
                                                        <span className="font-semibold">Fora da montra</span> · {m.showcase_label || 'motivo por apurar'}
                                                    </p>
                                                )}
                                                {m.showcase_override === 1 && (
                                                    <p className="mt-0.5 text-xs text-teal-700 max-w-[260px]">Na montra por decisão da equipa</p>
                                                )}
                                                {titleWarning(m.title) && (
                                                    <p className="mt-0.5 text-xs text-amber-700 max-w-[260px]" title={`${titleWarning(m.title)} Corrige em "Editar" antes de aprovar.`}>
                                                        <span className="font-semibold">Nome suspeito</span> · {titleWarning(m.title)}
                                                    </p>
                                                )}
                                                {m.ai_generated === 1 && m.ai_flag_reason && (
                                                    <p className="text-xs text-violet-700 max-w-[260px]" title={m.ai_flag_reason}>Motivo: {m.ai_flag_reason}</p>
                                                )}
                                                {m.ai_generated === 1 && m.ai_appeal_at && (
                                                    <p className="text-xs text-amber-700 max-w-[260px] whitespace-pre-wrap">
                                                        <strong>Contestada {formatDate(m.ai_appeal_at)}:</strong> {m.ai_appeal_note}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-gray-600 hidden lg:table-cell">{m.genre || '—'}</td>
                                            <td className="px-3 py-2 text-gray-600 hidden md:table-cell font-mono text-xs">{formatDuration(m.duration)}</td>
                                            <td className="px-3 py-2 text-gray-600 hidden md:table-cell">{(m.plays || 0).toLocaleString('pt-PT')}</td>
                                            <td className="px-3 py-2 text-gray-500 text-xs hidden lg:table-cell">{formatDate(m.upload_date)}</td>
                                            <td className="px-3 py-2">
                                                <StateBadge state={m.music_state} />
                                            </td>
                                            <td className="px-3 py-2 hidden lg:table-cell">
                                                <ActiveBadge active={m.active} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex gap-1 flex-wrap">
                                                    <button
                                                        onClick={() => setEditing(m)}
                                                        title={ACTION_HELP.edit}
                                                        className="text-xs text-gray-700 bg-gray-100 border border-gray-300 py-1 px-2 rounded hover:bg-gray-200 transition"
                                                    >
                                                        Editar
                                                    </button>
                                                    {m.music_state !== 'approved' && (
                                                        <button
                                                            onClick={() => openModal(m, 'approved')}
                                                            title={ACTION_HELP.approve}
                                                            className="text-xs text-blue-600 bg-blue-400/20 border border-blue-500 py-1 px-2 rounded hover:bg-blue-400/30 transition"
                                                        >
                                                            Aprovar
                                                        </button>
                                                    )}
                                                    {m.music_state !== 'rejected' && (
                                                        <button
                                                            onClick={() => openModal(m, 'rejected')}
                                                            title={ACTION_HELP.reject}
                                                            className="text-xs text-rose-600 bg-rose-400/20 border border-rose-500 py-1 px-2 rounded hover:bg-rose-400/30 transition"
                                                        >
                                                            Reprovar
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => setAiGenerated(m, m.ai_generated !== 1)}
                                                        className="text-xs text-violet-700 bg-violet-400/10 border border-violet-400 py-1 px-2 rounded hover:bg-violet-400/20 transition"
                                                        title={m.ai_generated === 1 ? 'Retirar a marca de IA' : 'Marcar como totalmente gerada por IA (sem royalties)'}
                                                    >
                                                        {m.ai_generated === 1 ? 'Retirar IA' : 'Marcar IA'}
                                                    </button>
                                                    {m.music_state === 'approved' && (
                                                        <button
                                                            onClick={() => setShowcase(m, m.showcase_override != null ? 'auto' : m.showcase === 1 ? 'out' : 'in')}
                                                            title={ACTION_HELP.showcase}
                                                            className="text-xs text-teal-700 bg-teal-400/10 border border-teal-500 py-1 px-2 rounded hover:bg-teal-400/20 transition"
                                                        >
                                                            {m.showcase_override != null ? 'Montra: automático' : m.showcase === 1 ? 'Tirar da montra' : 'Pôr na montra'}
                                                        </button>
                                                    )}
                                                    {m.release_id && (
                                                        <button
                                                            onClick={() => navigate(`/release/${m.release_id}`)}
                                                            title={ACTION_HELP.release}
                                                            className="text-xs text-purple-600 bg-purple-400/20 border border-purple-500 py-1 px-2 rounded hover:bg-purple-400/30 transition"
                                                        >
                                                            Release
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage <= 1
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                ← Anterior
                            </button>
                            <span className="text-sm text-gray-500">
                                Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                className={`text-sm font-medium px-3 py-1.5 rounded border transition ${
                                    currentPage >= totalPages
                                        ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                        : 'text-gray-600 border-gray-300 hover:border-amber-400 hover:text-amber-600'
                                }`}
                            >
                                Próxima →
                            </button>
                        </div>
                    )}
                </div>
            )}

            {editing && (
                <EditMusicModal
                    music={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(updated) => {
                        setMusics(list => list.map(x => (x.music_id === updated.music_id ? { ...x, ...updated } : x)));
                        setEditing(null);
                    }}
                />
            )}

            {/* Confirmation Modal */}
            {modal && modalData && (
                <div className="opt-modal-wrapper">
                    <div className="opt-modal-container" style={{ maxWidth: 420 }}>
                        <div className="opt-modal-content">
                            <div style={{ padding: '30px 24px', textAlign: 'center' }}>
                                <h3 style={{ color: '#fff', marginBottom: 8, fontSize: 18 }}>
                                    {modalAction === 'approved' ? 'Aprovar música' : 'Reprovar música'}
                                </h3>
                                <p style={{ color: '#aaa', fontSize: 14, marginBottom: 6 }}>
                                    <strong style={{ color: '#fff' }}>{modalData.title}</strong>
                                </p>
                                <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>
                                    por {modalData.singer || modalData.name}
                                </p>
                                <p style={{ color: '#ccc', fontSize: 13, marginBottom: 24 }}>
                                    Esta ação é irreversível. Tem certeza?
                                </p>
                                <div className="block-user-footer">
                                    <button onClick={() => setModal(false)}>
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={confirmAction}
                                        className={modalAction === 'approved' ? '' : 'block'}
                                        style={modalAction === 'approved' ? { backgroundColor: '#3b82f6', borderColor: '#3b82f6' } : undefined}
                                    >
                                        {modalAction === 'approved' ? 'Aprovar' : 'Reprovar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
